import React from 'react';
import { inject, observer } from 'mobx-react';
import type { AppStore } from '@stores/app/store';
import type { SiteStore } from '@stores/site/store';
import type { UserAction } from '@stores/user/action';
import type { ThreadAction } from '@stores/thread/action';
import PC from './layout/pc';
import H5 from './layout/h5';
import { BasePage } from '@components/Base/BasePage';
import styles from './index.module.scss';
import type { BasePageProps } from '../../types/base';
import { withRouter } from 'react-router-dom';
import { RouteComponentProps } from 'react-router-dom';
import type { BasePageState } from '../../types/base';
import { getCookie } from '@utils/cookie';
import Toast from '@components/Toast';

interface RouteParams {
    code: string;
}
interface UserInfo {
    code: string;
    nickname?: string;
    avatar?: string;
    follow?: number;
    isFollowed?: boolean;
    [key: string]: any;  // 其他可能的属性
}

interface UserProps extends BasePageProps, RouteComponentProps<RouteParams> {
    app?: AppStore;
    site?: SiteStore;
    user?: UserAction;
    thread?: ThreadAction;
}
interface UserState extends BasePageState {
    myValue: string;
    userInfo: UserInfo
    userMedalData: any
    navList: any
    navCurrentIndex: any
    navCurrentType: any
    postsList: any // 评论列表
    feedbackFilterIndex: number
    feedbackFilterList: any[]
    unreadNotificationsData: any
    // friendList: any // 关注/粉丝列表
    // friendType: any   // 类型（1是关注，为2是粉丝）
    // friendPage: any
}
@inject('app', 'site', 'user', 'thread')
@observer
class User extends BasePage<UserProps, UserState> {
    namespace = 'usersThreadsList';    // 用于记录当前正在加载的用户 code，防止竞态条件
    currentLoadingCode: string = '';
    currentLoadingNamespace: string = ''; // 新增：记录当前加载的namespace
    isLoadingMore: boolean = false; // 新增：防止重复加载
    state = {
        loading: false,
        error: null,
        pageLoaded: false,
        myValue: '',
        userInfo: { code: '' },  // 提供默认值
        userMedalData: {}, // 用户勋章
        navList: [
            {name: '发帖', id: 0, type: '0', namespace: 'usersThreadsList' },
            {name: '评论', id: 0, type: '1', namespace: 'usersPostsList'},
            {name: '点赞', id: 1, type: '2', namespace: 'usersLikesList'},
            {name: '收藏', id: 0, type: '3', namespace: 'usersFavoritesList'},
            {name: '反馈', id: 0, type: '8', namespace: 'usersFeedbacksList'},
            // {name: '勋章', id: 1, type: '4'},
            // {name: '关注', id: 5, type: '5', namespace: 'followUserList'},
            // {name: '粉丝', id: 6, type: '6', namespace: 'fansUserList'},
        ],  // tab切换列表
        navCurrentIndex: 0, // 导航当前选中索引
        navCurrentType: 0,  // 导航当前选中类型
        postsList: [],    // 评论列表
        feedbackFilterIndex: 0,
        feedbackFilterList: [
            { name: '全部', id: '', value: '' },
            { name: '已解决', id: 3, value: '3' },
            { name: '已回复', id: 2, value: '2' },
            { name: '待处理', id: 1, value: '1' },
            { name: '未解决', id: 0, value: '0' },
        ],
        unreadNotificationsData: {},    // 消息数据
        // friendList: [], // 关注/粉丝列表
        // friendType: 1,    // 为1是关注，为2是粉丝
        // friendPage: 1,  // 关注或粉丝列表的分页
    };

    shouldHideFavoritesTab = () => {
        const { match, user } = this.props;
        const loginUserCode = getCookie('user_code') || user?.userInfo?.code;
        const isSelf = !!loginUserCode && match.params?.code == loginUserCode;
        // 与页面上的关注区域一致：他人中心显示关注区域，因此隐藏收藏tab
        return !isSelf;
    }

    getDisplayNavList = () => {
        const { navList } = this.state;
        const platform = this.props.app?.platform || 'h5';
        let list = navList;

        // 反馈 tab 仅 PC 端显示
        if (platform !== 'pc') {
            list = list.filter((item: any) => item?.namespace !== 'usersFeedbacksList');
        }

        if (!this.shouldHideFavoritesTab()) return list;
        return list.filter((item: any) => item?.namespace !== 'usersFavoritesList' && item?.namespace !== 'usersFeedbacksList');
    }
    // 从路由 query 参数中读取 tab，映射为 navCurrentIndex
    // tab=1发帖, 2评论, 3点赞, 4收藏, 5反馈 → index: 0,1,2,3,4
    getTabFromRoute = (): number => {
        const { location } = this.props;
        if (!location || !location.search) return 0;
        const params = new URLSearchParams(location.search);
        const tab = parseInt(params.get('tab') || '0', 10);
        if (tab >= 1 && tab <= 5) return tab - 1;
        return 0;
    }

    /**
     * 获取用户详情；用户不存在时返回首页，并忽略已经切换用户后的旧请求结果。
     */
    loadUserInfo = (code: string) => {
        const { user } = this.props;
        if (!user) return;

        user.getUserInfo({ code }).then((res: any) => {
            // 用户快速切换时，旧请求不能覆盖当前页面或触发错误跳转。
            if (this.currentLoadingCode !== code || this.props.match.params.code !== code) {
                return;
            }

            // 接口返回 404 表示用户不存在，返回首页并结束当前页面后续加载。
            if (Number(res?.code ?? res?.Code) === 404) {
                this.props.history.replace('/');
                return;
            }

            if (Number(res?.code ?? res?.Code) === 0) {
                this.setState({
                    userInfo: res.data
                });
                // 用户详情成功后，按当前 tab 加载对应列表。
                this.loadTabDataByNamespace(res.data?.code || code);
            }
        });
    }

    componentDidMount() {
        const { app, user, thread, match } = this.props;
        // 获取路由参数 id
        const code = match.params.code;
        
        // 记录当前加载的用户 code，用于防止请求串联
        this.currentLoadingCode = code;

        // 从路由 tab 参数初始化 tab 索引
        const initialTabIndex = this.getTabFromRoute();
        if (initialTabIndex !== this.state.navCurrentIndex) {
            this.setState({
                navCurrentIndex: initialTabIndex,
                navCurrentType: this.state.navList[initialTabIndex]?.type || 0,
            });
        }
        
        // 进入 user 页面时，清空用户页面相关数据，避免污染（保留登录状态）
        if (user && user.clearUserPageData) {
            user.clearUserPageData();
        }

        // 在获取数据前先重置列表
        this.resetList();

        // 获取用户详情；不存在的用户由统一处理方法跳转首页。
        this.loadUserInfo(code);

        if (user) {
            // let params = {
            //     'vivo[user_code]': code,
            //     'switch[ta_medal]': 1,
            //     'ta_medal[user_code]': code
            // }
            user.getUserMedal({ code: code }).then(res => {
                if (res.code == 0) {
                    this.setState({
                        userMedalData: res.data
                    })
                }

            });


        }
        // if (thread) {
        //     let params: any = {
        //         "page[number]": 1,
        //         "page[size]": 100
        //     }
        //     thread.getThreadList(params)
        // }
        this.getUnreadNotifications()
        super.componentDidMount();
    }

    componentDidUpdate(prevProps: UserProps, prevState: UserState) {
        if (prevProps.match.params.code !== this.props.match.params.code) {
            const { user, match } = this.props;
            const code = match.params.code;
            
            // 更新当前加载的用户 code
            this.currentLoadingCode = code;
            
            if (user && user.clearAllUserData) {
                user.clearAllUserData();
            }

            // 重置列表
            this.resetList();

            // 从路由 tab 参数初始化 tab 索引
            const routeTabIndex = this.getTabFromRoute();
            if (routeTabIndex !== this.state.navCurrentIndex) {
                this.setState({
                    navCurrentIndex: routeTabIndex,
                    navCurrentType: this.state.navList[routeTabIndex]?.type || 0,
                });
            }

            // 重新获取新用户的信息
            if (user) {
                // 重新获取用户详情；不存在的用户由统一处理方法跳转首页。
                this.loadUserInfo(code);

                // 重新获取用户勋章
                user.getUserMedal({ code: code }).then(res => {
                    if (res.code == 0) {
                        this.setState({
                            userMedalData: res.data
                        });
                    }
                });
            }
        }
        // 如果tab切换，也需要重置加载状态
        if (prevState?.navCurrentIndex !== this.state.navCurrentIndex) {
            this.isLoadingMore = false;
            this.currentLoadingNamespace = this.state.navList[this.state.navCurrentIndex]?.namespace;
        }

        // 当“收藏”tab被隐藏时，若当前仍选中收藏，自动回到第一个tab
        const displayNavList = this.getDisplayNavList();
        const currentNav = this.state.navList[this.state.navCurrentIndex];
        const isFavoritesCurrent = currentNav?.namespace === 'usersFavoritesList';
        const hasFavoritesInDisplay = displayNavList.some((item: any) => item?.namespace === 'usersFavoritesList');
        if (isFavoritesCurrent && !hasFavoritesInDisplay) {
            this.setState({
                navCurrentIndex: 0,
                navCurrentType: this.state.navList[0]?.type || 0
            }, () => {
                this.loadTabData();
            });
        }

        // H5 不显示反馈 tab，若当前仍选中反馈，自动回到第一个 tab
        const isFeedbackCurrent = currentNav?.namespace === 'usersFeedbacksList';
        const hasFeedbackInDisplay = displayNavList.some((item: any) => item?.namespace === 'usersFeedbacksList');
        if (isFeedbackCurrent && !hasFeedbackInDisplay) {
            this.setState({
                navCurrentIndex: 0,
                navCurrentType: this.state.navList[0]?.type || 0
            }, () => {
                this.loadTabData();
            });
        }
    }
    componentWillUnmount(): void {
        const { user, thread } = this.props;
        if (user?.listStore?.clearList) {
            user.listStore.clearList({ namespace: 'usersThreadsList' });
            user.listStore.clearList({ namespace: 'usersPostsList' });
            user.listStore.clearList({ namespace: 'usersLikesList' });
            user.listStore.clearList({ namespace: 'usersFavoritesList' });
        }
        // 反馈列表数据在 thread.listStore 中
        thread?.listStore?.clearList({ namespace: 'usersFeedbacksList' });
    }

    // 按指定 namespace 加载 tab 数据；未指定时取当前选中的 tab
    loadTabDataByNamespace = (code: string, targetNamespace?: string) => {
        const { user, thread } = this.props;
        const { navList, navCurrentIndex } = this.state;

        if (!user || !code) return;

        const namespace = targetNamespace || navList[navCurrentIndex]?.namespace;
        if (!namespace) return;

        const params = {
            'page[number]': 1,
            'page[size]': 20
        };

        switch (namespace) {
            case 'usersThreadsList':
                user.getUsersThreadsList(code, params);
                break;
            case 'usersPostsList':
                user.getUsersPostsList(code, params).then((res: any) => {
                    if (res.code === 0) {
                        this.setState({
                            postsList: res.data || []
                        });
                    }
                });
                break;
            case 'usersLikesList':
                user.getUsersLikesList(code, params);
                break;
            case 'usersFavoritesList':
                user.getUsersFavoritesList(code, params);
                break;
            case 'usersFeedbacksList':
                const feedbackFilterValue1 = this.getFeedbackFilterValue();
                const feedbackParams: any = {
                    ...params,
                    'filter[mine]': 1,
                };
                if (feedbackFilterValue1 !== null) {
                    feedbackParams['filter[solveState]'] = feedbackFilterValue1;
                }
                thread.getFeedbackList(feedbackParams, 'usersFeedbacksList');
                break;
            default:
                break;
        }
    }

    // 获取未读消息数
    getUnreadNotifications = async () => {
        const { app, user } = this.props;
        const { navList } = this.state;
        if(user) {
            const res = await user.getUnreadNotifications();
            console.log(res, '未读消息数');
            this.setState({
                unreadNotificationsData: res.data
            })
        }
    }

    // 清空 threadList，避免复用上一次列表数据
    resetList = () => {
        const store = this.props.user?.listStore;
        const threadStore = this.props.thread?.listStore;
        if (!store?.clearList) return;
        // 全量清空列表，避免残留分页数据
        store.clearList({ namespace: 'usersThreadsList' });
        store.clearList({ namespace: 'usersPostsList' });
        store.clearList({ namespace: 'usersLikesList' });
        store.clearList({ namespace: 'usersFavoritesList' });
        // 反馈列表数据在 thread.listStore 中
        threadStore?.clearList({ namespace: 'usersFeedbacksList' });
        store.setAttribute?.({
            namespace: 'usersThreadsList',
            key: 'requestError',
            value: { isError: false, errorText: '' }
        });
        store.setAttribute?.({
            namespace: 'usersPostsList',
            key: 'requestError',
            value: { isError: false, errorText: '' }
        });
        store.setAttribute?.({
            namespace: 'usersLikesList',
            key: 'requestError',
            value: { isError: false, errorText: '' }
        });
        store.setAttribute?.({
            namespace: 'usersFavoritesList',
            key: 'requestError',
            value: { isError: false, errorText: '' }
        });
        threadStore?.setAttribute?.({
            namespace: 'usersFeedbacksList',
            key: 'requestError',
            value: { isError: false, errorText: '' }
        });
    }

    // 分页
    // handleLoadMore = () => {
    //     const { user, match } = this.props;
    //     let { userInfo, navCurrentIndex } = this.state;

    //     const code = match.params.code || userInfo.code;

    //     if (!user || !code) return;

    //     const list = user.listStore.lists['usersThreadsList'];
    //     if (!list) return;

    //     const currentPage = list.attribs?.currentPage || list.attribs?.page || 1;
    //     const nextPage = currentPage + 1;
    //     const pageSize = list.attribs?.pageSize || 20;

    //     let params: any = {
    //         "page[number]": nextPage,
    //         "page[size]": pageSize
    //     }

    //     // 保存当前的 code，用于在回调时验证用户是否切换
    //     const requestCode = code;
    //     this.currentLoadingCode = code; // 记录当前请求的用户
    //     if(navCurrentIndex == 0) { // 是用户帖子列表
    //         user.getUsersThreadsList(code, params).then((res: any) => {
    //             // 检查用户是否已经切换，如果切换了，则不追加数据
    //             const currentCode = this.props.match.params.code || this.state.userInfo.code;
    //             if (requestCode !== currentCode || this.currentLoadingCode !== code) {
    //                 console.log('用户已切换，忽略旧的加载请求');
    //                 return;
    //             }
    //         })
    //     } else if (navCurrentIndex == 1) {  // 是用户评论列表
    //         user.getUsersPostsList(code, params).then((res: any) => {
    //             // 检查用户是否已经切换，如果切换了，则不追加数据
    //             console.log(res, '用户评论列表')
    //             const currentCode = this.props.match.params.code || this.state.userInfo.code;
    //             if (requestCode !== currentCode || this.currentLoadingCode !== code) {
    //                 console.log('用户已切换，忽略旧的加载请求');
    //                 return;
    //             }
    //         })
    //     }
        
    // }

    // 分页 - 支持多tab
    handleLoadMore = () => {
        const { user, match, thread } = this.props;
        let { userInfo, navCurrentIndex, navList } = this.state;

        const code = match.params.code || userInfo.code;

        if (!user || !code) {
            return;
        }

        // 获取当前选中的tab信息
        const currentTab = navList[navCurrentIndex];
        
        if (!currentTab || !currentTab.namespace) {
            return;
        }

        const namespace = currentTab.namespace;

        // 反馈列表数据存储在 thread.listStore，其他列表在 user.listStore
        const store = namespace === 'usersFeedbacksList' ? thread?.listStore : user.listStore;
        const list = store?.lists[namespace];

        if (!list) {
            return;
        }

        // 获取分页信息
        const currentPage = list.attribs?.currentPage || list.attribs?.page || 1;
        const totalPages = list.attribs?.totalPages || list.attribs?.pageCount || 1;
        
        // 判断是否还有更多数据 - 优先使用 hasMore 属性
        if (!list.hasMore) {
            return;
        }

        // 防止重复加载
        if (this.isLoadingMore) {
            return;
        }

        const nextPage = currentPage + 1;
        const pageSize = list.attribs?.pageSize || 20;

        this.isLoadingMore = true;

        const params: any = {
            "page[number]": nextPage,
            "page[size]": pageSize
        };

        // 保存当前的 code 和 namespace，用于在回调时验证
        const requestCode = code;
        const requestNamespace = namespace;
        this.currentLoadingCode = code;
        this.currentLoadingNamespace = namespace;

        // 根据不同的namespace调用不同的API
        let apiCall: Promise<any> | null = null;

        switch (namespace) {
            case 'usersThreadsList':
                apiCall = user.getUsersThreadsList(code, params);
                break;
            case 'usersPostsList':
                apiCall = user.getUsersPostsList(code, params);
                break;
            case 'usersLikesList':
                apiCall = user.getUsersLikesList(code, params);
                break;
            case 'usersFavoritesList':
                apiCall = user.getUsersFavoritesList(code, params);
                break;
            case 'usersFeedbacksList':
                const feedbackFilterValue2 = this.getFeedbackFilterValue();
                const fbParams: any = {
                    ...params,
                    'filter[mine]': 1,
                };
                if (feedbackFilterValue2 !== null) {
                    fbParams['filter[solveState]'] = feedbackFilterValue2;
                }
                apiCall = thread.getFeedbackList(fbParams, 'usersFeedbacksList');
                break;
            // case 'followUserList':
            //     // apiCall = user.getFollowUserList(code, params);
            //     console.log('关注列表API待实现');
            //     break;
            // case 'fansUserList':
            //     // apiCall = user.getFansUserList(code, params);
            //     console.log('粉丝列表API待实现');
            //     break;
            // case 'userLikeList':
            //     // apiCall = user.getUserLikeList(code, params);
            //     console.log('点赞列表API待实现');
            //     break;
            // case 'userFavoriteList':
            //     // apiCall = user.getUserFavoriteList(code, params);
            //     console.log('收藏列表API待实现');
            //     break;
            // case 'userMedalList':
            //     // apiCall = user.getUserMedalList(code, params);
            //     console.log('勋章列表API待实现');
            //     break;
            default:
                console.log(`未知的namespace: ${namespace}`);
                this.isLoadingMore = false;
                return;
        }

        if (!apiCall) {
            this.isLoadingMore = false;
            return;
        }

        apiCall
            .then((res: any) => {
                // 检查用户或tab是否已经切换
                const currentCode = this.props.match.params.code || this.state.userInfo.code;
                const currentTab = this.state.navList[this.state.navCurrentIndex];
                
                if (requestCode !== currentCode || 
                    this.currentLoadingCode !== code || 
                    requestNamespace !== currentTab?.namespace ||
                    this.currentLoadingNamespace !== currentTab?.namespace) {
                    console.log('用户或tab已切换，忽略旧的加载请求');
                    return;
                }

                console.log(`${namespace} 加载更多成功`, res);
                
                // 可以根据需要更新特定的state
                if (namespace === 'usersPostsList' && res.data) {
                    this.setState({
                        postsList: [...this.state.postsList, ...res.data]
                    });
                }
                // 可以添加其他namespace的数据处理
                
            })
            .catch((error: any) => {
                console.error(`${namespace} 加载更多失败`, error);
            })
            .finally(() => {
                this.isLoadingMore = false;
            });
    };

    // 获取关注、粉丝列表，userType为1是关注列表，为2是粉丝列表
    // getFriendList = async () => {
    //     const { user } = this.props;
    //     const { friendPage, friendType, friendList } = this.state;

    //     if(user) {
    //         let params:any = {
    //             page: friendPage,
    //             perPage: 20,
    //             'filter[type]': friendType
    //         }
    //         const res:any = await user.fetchFollowList(params)
    //         console.log(res, '关注列表');
    //         if(res.code == 0) {
    //             // 如果是第一页，替换数据；否则追加数据
    //             const newFriendList = friendPage === 1 ? res.data?.data : [...friendList, ...res.data?.data];
    //             if(friendType == 1) { // 是关注列表
    //                 this.setState({
    //                     friendList: newFriendList,
    //                 })
    //             }
                
    //         }
    //     }
    // }

    // 关注用户
    createFollowUser = () => {
        const { user } = this.props;
        const { userInfo } = this.state;
        if (user) {
            let params: any = {
                toUserCode: userInfo?.code
            }
            user.createFollowUser(params).then(res => {
                if (res.code == 0) {
                    user.getUserInfo({ code: userInfo?.code }).then(res => {
                        this.setState({
                            userInfo: res.data
                        })
                    });
                }

            })

        }
    }

    // 取消关注用户
    followDelete = () => {
        const { user } = this.props;
        const { userInfo } = this.state;
        if (user) {
            let params: any = {
                code: userInfo?.code,
                type: 1
            }
            user.followDelete(params).then(res => {
                if (res.code == 0) {
                    user.followDelete(params).then(res => {
                        if (res.code == 0) {
                            user.getUserInfo({ code: userInfo?.code }).then(res => {
                                this.setState({
                                    userInfo: res.data
                                })
                            });
                        }

                    })
                }
            })
        }
    }

    // 打开私信对话框页面
    openDialog = () => {
        const { user } = this.props;
        const { userInfo } = this.state;
        if (user) {
            let params: any = {
                code: userInfo?.code,
                type: 1
            }
            user.getChatRoomFind(params).then(res => {
                if (res.code == 0) {
                    console.log(res, '获取roomId')
                    this.props.history.push(`/privateMessage?roomId=${res.data?.roomId}&usercode=${userInfo?.code}`);
                }
            })
        }
        
    }

    // 跳转到话题详情页
    handleTopicClick = (id: string | number) => {
        this.props.history && this.props.history.push(`/topicDetail/${id}`);
    };

    // 只获取数据，不渲染
    getThreadListData = () => {
        const { thread } = this.props;
        if (!thread || !thread.listStore) return [];
        const list = thread.listStore.lists[this.namespace];
        if (!list) return [];
        return thread.listStore.getList({ namespace: this.namespace });
    }

    // 导航点击，类型切换
    // navClick = (item: any, index: any) => {
    //     console.log('tab切换', item.type, index);
    //     // 全部初始化
    //     this.namespace = item.namespace;
    //     this.setState({
    //         navCurrentIndex: index,
    //         navCurrentType: item.type,
            
    //     }, () => {
    //         if(index == 0) {    // 是发帖
                
    //         } else if(index == 1) { // 是评论
    //             this.setState({
                    
    //             }, () => {
                    
    //             })
    //         } else if(index == 2) { // 是收藏
    //             this.setState({
                    
    //             }, () => {
                    
    //             })
    //         }
    //     })

    // }
    // 导航点击，类型切换
    navClick = (item: any, index: any) => {
        // 如果点击的是当前选中的tab，不处理
        if (this.state.navCurrentIndex === index) {
            return;
        }
        
        // 更新命名空间
        this.namespace = item.namespace;
        
        // 重置加载状态
        this.isLoadingMore = false;
        this.currentLoadingNamespace = item.namespace;
        
        // 更新state
        this.setState({
            navCurrentIndex: index,
            navCurrentType: item.type,
            // 切换tab时清空对应的列表数据，避免显示旧数据
            postsList: index === 1 ? [] : this.state.postsList, // 评论列表
            // 可以添加其他列表的清空
        }, () => {
            // 切换tab后加载对应的数据
            this.loadTabData();
        });
    }

    // 加载tab数据
    loadTabData = () => {
        const { user, match } = this.props;
        const { navCurrentIndex, navList, userInfo } = this.state;
        
        const code = match.params.code || userInfo.code;
        if (!user || !code) return;
        
        const currentTab = navList[navCurrentIndex];
        if (!currentTab) return;

        this.loadTabDataByNamespace(code, currentTab.namespace);
    }

    // 获取反馈列表筛选参数值：取当前 feedbackFilterList 项的 value
    getFeedbackFilterValue = () => {
        const { feedbackFilterList, feedbackFilterIndex } = this.state;
        const currentFilter = feedbackFilterList[feedbackFilterIndex];
        if (!currentFilter || currentFilter.value === undefined || currentFilter.value === '') {
            return null;
        }
        return Number(currentFilter.value);
    }

    // 反馈列表筛选切换
    handleFeedbackFilterChange = (index: number) => {
        const { user, match, thread } = this.props;
        const { userInfo } = this.state;
        const code = match.params.code || userInfo.code;

        this.setState({ feedbackFilterIndex: index }, () => {
            // 清空反馈列表并重新加载
            thread?.listStore?.clearList({ namespace: 'usersFeedbacksList' });
            this.loadTabDataByNamespace(code, 'usersFeedbacksList');
        });
    }

    render() {
        const { app } = this.props;
        let { userInfo, userMedalData, navList, navCurrentIndex, navCurrentType, postsList, feedbackFilterIndex, feedbackFilterList, unreadNotificationsData } = this.state;
        const displayNavList = this.getDisplayNavList();
        const safeNavCurrentIndex = displayNavList[navCurrentIndex] ? navCurrentIndex : 0;
        const platform = app?.platform || 'h5';
        // 如果平台信息还未初始化，显示加载状态
        if (!app?.platform) {
            return (
                <div className={styles.loading} style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh'
                }}>
                    <div>加载中...</div>
                </div>
            );
        } else {
            return (<div className={platform === 'pc' ? styles.pcWrap : styles.h5Wrap}>
                {platform === 'pc' ? (<PC
                    userInfo={userInfo}
                    createFollowUser={() => this.createFollowUser()}
                    followDelete={() => this.followDelete()}
                    userMedalData={userMedalData}
                    handleLoadMore={this.handleLoadMore}
                    navList={displayNavList}
                    navCurrentIndex={safeNavCurrentIndex}
                    navCurrentType={navCurrentType}
                    navClick={(item, index) => this.navClick(item, index)}
                    postsList={postsList}
                    openDialog={() => this.openDialog()}
                    feedbackFilterIndex={feedbackFilterIndex}
                    feedbackFilterList={feedbackFilterList}
                    unreadNotificationsData={unreadNotificationsData}
                    onFeedbackFilterChange={(index: number) => this.handleFeedbackFilterChange(index)}
                ></PC>) : (
                    <H5
                        userInfo={userInfo}
                        userMedalData={userMedalData}
                        createFollowUser={() => this.createFollowUser()}
                        followDelete={() => this.followDelete()}
                        handleLoadMore={this.handleLoadMore}
                        navList={displayNavList}
                        navCurrentIndex={safeNavCurrentIndex}
                        navCurrentType={navCurrentType}
                        navClick={(item, index) => this.navClick(item, index)}
                        postsList={postsList}
                        openDialog={() => this.openDialog()}
                    ></H5>
                )}
            </div>)
        }

    }
}

export default withRouter(User);
